import React, { memo } from "react";
import Spacer from "../../Spacer";
import { useTranslation } from "react-i18next";
import { isPassingHrSignalQuality } from "../../../utils/results/signalQuality";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import { SHOW_FULL_RESULTS } from "../../../config/results";

function formatQuality(value, t) {
  if (value === "fail") return t("common.fail", "Fail");
  return t("common.pass", "Pass");
}

export default memo(function CardiopulmonaryResultRow({
  time,
  date,
  rr,
  hr,
  rrQuality,
  hrQuality,
  onClick,
}) {
  const { t } = useTranslation();

  const rrIsBad = rrQuality === "fail";
  const hrIsBad = hrQuality != null && !isPassingHrSignalQuality(hrQuality);
  const allQualityIsPass = !rrIsBad && !hrIsBad;
  const metricTextClass = allQualityIsPass
    ? "!text-primary"
    : "!text-greyscale-3";

  const renderMetric = (label, value, unit = null) => (
    <div className="flex flex-col items-center justify-start min-w-0 self-stretch gap-0">
      <div className="w-full flex items-start justify-center text-center leading-tight">
        <p>
          {label} <span className="muted-text text-xs">{unit}</span>
        </p>
      </div>
      <div
        className={`title-text text-center whitespace-nowrap ${metricTextClass}`}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col">
      <button
        type="button"
        onClick={onClick}
        className={[
          "w-full text-left py-2 flex-col items-center gap-4 bg-transparent",
          onClick ? "cursor-pointer" : "cursor-default",
        ].join(" ")}
        disabled={!onClick}
      >
        {/* Top: timestamp */}
        <div>
          <p className="muted-text">
            {time} {date}
          </p>
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {SHOW_FULL_RESULTS ? (
            <div className="grid grid-cols-2 gap-2 items-stretch">
              {renderMetric(
                t("result.pulse.hrLabel", "Heart Rate"),
                formatValidatedMeasurement("hr", hr),
                t("result.pulse.unit", "bpm"),
              )}
              {renderMetric(
                t("results.resp.rrLabel", "RR"),
                formatValidatedMeasurement("rr", rr),
                t("results.resp.rrUnit", "bpm"),
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1 items-stretch">
              <div className="flex flex-col text-center items-center justify-between min-w-0 self-stretch">
                <div className="w-full flex items-start justify-center text-center leading-tight">
                  <p>
                    {t(
                      "result.cardiopulmonary.cardiacQuality",
                      "Cardiac Quality",
                    )}
                  </p>
                </div>
                <div
                  className={`title-text whitespace-nowrap ${allQualityIsPass ? "!text-primary" : "!text-greyscale-3"}`}
                >
                  {formatQuality(hrIsBad ? "fail" : "pass", t)}
                </div>
              </div>

              <div className="flex flex-col text-center items-center justify-between min-w-0 self-stretch">
                <div className="w-full flex items-start justify-center text-center leading-tight">
                  <p>
                    {t(
                      "result.cardiopulmonary.respiratoryQuality",
                      "Respiratory Quality",
                    )}
                  </p>
                </div>
                <div
                  className={`title-text whitespace-nowrap ${allQualityIsPass ? "!text-primary" : "!text-greyscale-3"}`}
                >
                  {formatQuality(rrIsBad ? "fail" : "pass", t)}
                </div>
              </div>
            </div>
          )}

          {!allQualityIsPass ? (
            <div className="muted-text text-center text-xs leading-tight">
              {t("results.quality.low", "Low signal quality")}
            </div>
          ) : null}
        </div>
      </button>

      <Spacer />
    </div>
  );
});
